import useIsInViewport from 'hooks/isInViewport';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import apiFetch from 'lib/apiFetch';
import { renderAdContent } from './helpers/renderAdContent';

export enum POSITION {
    top = 'top',
    right = 'right'
}
type Props = {
    adTextPosition?: POSITION;
    slotId?: string;
    desktopMinWidth?: number;
    onRenderComplete?: () => void;
};

interface SlotProps {
    $position?: POSITION;
    $isRendered: boolean;
}

const AdTextPositionTop = css`
    top: -14px;
    left: 0;
`;

const AdTextPositionRight = css`
    top: 15px;
    right: -30px;
    transform: rotate(90deg);
    -webkit-transform: rotate(90deg);
    -moz-transform: rotate(90deg);
    -ms-transform: rotate(90deg);
    -o-transform: rotate(90deg);
`;

const Slot = styled.div<SlotProps>`
    &::after {
        content: 'Reklama';
        ${(props) => !props.$isRendered && `display: none`};
        font-weight: 300;
        font-size: 12px;
        line-height: 18px;
        color: ${({ theme }) => theme.colors.blueGrey};
        position: absolute;
        @media (min-width: 100px) {
            ${(props) =>
                props.$position === POSITION.top ? AdTextPositionTop : AdTextPositionRight}
        }
        top: -15px;
        right: 0;
    }
    position: relative;
    display: flex;
    justify-content: center;
    height: 100%;
    width: 100%;
`;

const fetchAdConfig = (url: string, requestPath: string) =>
    apiFetch
        .get(url, {
            params: { requestPath }
        })
        .then((res) => res.data);

const AdsSlot = ({
    slotId,
    desktopMinWidth = 992,
    adTextPosition = POSITION.right,
    onRenderComplete
}: Props) => {
    const router = useRouter();
    const [isRendered, setIsRendered] = useState(false);
    const adSlotRef = useRef<HTMLDivElement>(null);
    const isInViewport = useIsInViewport(adSlotRef);

    useEffect(() => {
        if (!isInViewport) return;

        const timer = setTimeout(() => {
            apiFetch.post(`/naszadsik/${slotId}/event`, { type: 'view', ip: '' });
        }, 1000);

        return () => {
            clearTimeout(timer);
        };
    }, [isInViewport, slotId]);

    const handleRenderComplete = () => {
        setIsRendered(true);
        if (onRenderComplete) onRenderComplete();
    };

    const { data, isLoading } = useSWR(
        isInViewport || isRendered ? [`/naszadsik/${slotId}/config`, router.asPath] : null,
        ([url, requestPath]) => fetchAdConfig(url, requestPath)
    );

    const handleClick = () => {
        apiFetch.post(`/naszadsik/${slotId}/event`, { type: 'click', ip: '' });
    };

    const content =
        !isLoading &&
        renderAdContent({ data, desktopMinWidth, onRenderComplete: handleRenderComplete });

    return (
        <Slot
            id={slotId}
            ref={adSlotRef}
            $position={adTextPosition}
            $isRendered={isRendered}
            onClick={handleClick}
        >
            {data?.link ? (
                <a href={data.link} target="_blank" rel="noopener noreferrer">
                    {content}
                </a>
            ) : (
                content
            )}
        </Slot>
    );
};

export default AdsSlot;
